/**
 * useAutoCapture
 *
 * Headless hook. Tracks how long the pose has been continuously "OK" and
 * fires a shutter callback after STABLE_MS of sustained good pose.
 *
 * Returns:
 *   progress  0–1  — fraction of stable window elapsed (for progress ring)
 *   armed     bool — true once stability ≥ ARM_MS (0.5 s); used by VoicePrompter
 *   fire()         — call to trigger the shutter manually (normally auto)
 */

import { useCallback, useEffect, useRef, useState } from "react";

const STABLE_MS = 2000; // auto-capture after this long (countdown mode)
const ARM_MS    =  250; // "armed" state (triggers "Stand still"); also shutter delay in instant mode
const TICK_MS   =   50; // RAF-like polling interval

export function useAutoCapture(
  poseOK: boolean,
  onCapture: () => boolean | void | Promise<boolean | void>,
  enabled: boolean = true,
  instantCapture: boolean = false
) {
  const [progress, setProgress] = useState(0);
  const [armed,    setArmed]    = useState(false);

  const stableFrom  = useRef<number | null>(null);
  const firedRef    = useRef(false);
  const enabledRef       = useRef(enabled);
  const poseOKRef        = useRef(poseOK);
  const onCaptureRef     = useRef(onCapture);
  const instantCaptureRef = useRef(instantCapture);

  useEffect(() => { enabledRef.current        = enabled;        }, [enabled]);
  useEffect(() => { poseOKRef.current          = poseOK;         }, [poseOK]);
  useEffect(() => { onCaptureRef.current       = onCapture;      }, [onCapture]);
  useEffect(() => { instantCaptureRef.current  = instantCapture; }, [instantCapture]);

  // Reset when poseOK flips to false
  useEffect(() => {
    if (!poseOK) {
      stableFrom.current = null;
      firedRef.current   = false;
      setProgress(0);
      setArmed(false);
    }
  }, [poseOK]);

  // Polling tick
  useEffect(() => {
    if (!enabled) return;

    const id = setInterval(() => {
      if (firedRef.current || !enabledRef.current) return;

      if (!poseOKRef.current) {
        stableFrom.current = null;
        setProgress(0);
        setArmed(false);
        return;
      }

      const now = Date.now();
      if (!stableFrom.current) stableFrom.current = now;

      const elapsed   = now - stableFrom.current;
      const fireAfter = instantCaptureRef.current ? ARM_MS : STABLE_MS;
      const p = Math.min(elapsed / fireAfter, 1);
      setProgress(p);
      setArmed(elapsed >= ARM_MS);

      if (elapsed >= fireAfter && !firedRef.current) {
        firedRef.current = true;
        Promise.resolve(onCaptureRef.current()).then((accepted) => {
          if (accepted === false) {
            firedRef.current = false;
            stableFrom.current = Date.now();
            setProgress(0);
            setArmed(false);
          }
        });
      }
    }, TICK_MS);

    return () => clearInterval(id);
  }, [enabled]);

  const fire = useCallback(() => {
    if (!firedRef.current) {
      firedRef.current = true;
      Promise.resolve(onCaptureRef.current()).then((accepted) => {
        if (accepted === false) {
          firedRef.current = false;
          stableFrom.current = Date.now();
          setProgress(0);
          setArmed(false);
        }
      });
    }
  }, []);

  /** Reset so the same hook can be reused for the next pose. */
  const reset = useCallback(() => {
    stableFrom.current = null;
    firedRef.current   = false;
    setProgress(0);
    setArmed(false);
  }, []);

  return { progress, armed, fire, reset };
}
